import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { ShoppingBag, Menu, X, ChevronDown, Phone, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from './Logo';

const flags = { de: '🇩🇪', en: '🇬🇧', it: '🇮🇹' };
const langLabels = { de: 'Deutsch', en: 'English', it: 'Italiano' };

export default function Header() {
  const { t, lang, switchLang } = useLanguage();
  const { itemCount, setIsOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  const isAdmin = location.pathname.startsWith('/admin');
  if (isAdmin) return null;

  const navLinks = [
    { to: '/', label: t('nav_home') },
    { to: '/menu', label: t('nav_menu') },
    { to: '/reservation', label: t('nav_reservation') },
    { to: '/order', label: t('nav_order') },
    { to: '/reviews', label: t('nav_reviews') },
    { to: '/about', label: t('nav_about') },
    { to: '/contact', label: t('nav_contact') },
  ];

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          scrolled ? 'bg-[hsl(38,44%,96%)]/95 backdrop-blur-md shadow-sm border-b border-[hsl(32,17%,84%)]' : 'bg-transparent'
        }`}
      >
        {/* Top bar */}
        <div className="hidden lg:block border-b border-[hsl(32,17%,84%)]/50">
          <div className="mx-auto max-w-[72rem] px-4 sm:px-6 lg:px-8 flex items-center justify-between py-1.5 text-xs text-[hsl(22,10%,38%)]">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> 0179/7007958</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Di-Sa 11:30-14:00 & 17:00-22:00 | So 11:30-22:00</span>
            </div>
            <a href="https://www.lieferando.de/speisekarte/little-italy-eberbach" target="_blank" rel="noopener noreferrer" className="text-[hsl(16,55%,36%)] font-medium hover:underline" data-testid="header-lieferando-link">
              {t('nav_lieferando')} →
            </a>
          </div>
        </div>

        {/* Main nav */}
        <div className="mx-auto max-w-[72rem] px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="nav-home-link">
              <Logo size="small" className="text-[hsl(20,17%,12%)]" />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`nav-${link.to.replace('/', '') || 'home'}-link`}
                  className={`px-3 py-2 rounded-[10px] text-sm font-medium transition-colors duration-150 ${
                    location.pathname === link.to
                      ? 'bg-[hsl(35,20%,92%)] text-[hsl(20,17%,12%)]'
                      : 'text-[hsl(22,10%,38%)] hover:bg-[hsl(35,20%,92%)]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-2">
              {/* Language Switcher */}
              <div className="relative">
                <button
                  data-testid="language-switcher-button"
                  onClick={() => setLangOpen(!langOpen)}
                  className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm hover:bg-[hsl(35,20%,92%)] transition-colors duration-150"
                >
                  <span className="text-base">{flags[lang]}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
                <AnimatePresence>
                  {langOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-1 w-36 bg-[hsl(36,33%,98%)] border border-[hsl(32,17%,84%)] rounded-xl shadow-lg overflow-hidden z-50"
                    >
                      {Object.entries(flags).map(([code, flag]) => (
                        <button
                          key={code}
                          data-testid={`language-option-${code}`}
                          onClick={() => { switchLang(code); setLangOpen(false); }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[hsl(35,20%,92%)] transition-colors duration-150 ${
                            lang === code ? 'bg-[hsl(35,20%,92%)] font-medium' : ''
                          }`}
                        >
                          <span className="text-base">{flag}</span>
                          <span>{langLabels[code]}</span>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Reserve CTA */}
              <Link
                to="/reservation"
                data-testid="header-reserve-cta"
                className="hidden sm:inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium bg-[hsl(16,55%,36%)] text-[hsl(38,44%,96%)] shadow-[0_10px_24px_rgba(33,24,16,.10)] hover:brightness-[1.03] transition-colors duration-200"
              >
                {t('hero_cta_reserve')}
              </Link>

              {/* Cart */}
              <button
                data-testid="cart-open-button"
                onClick={() => setIsOpen(true)}
                className={`relative p-2 rounded-lg transition-colors duration-150 ${itemCount > 0 ? 'bg-[hsl(16,55%,36%)]/10 ring-1 ring-[hsl(16,55%,36%)]/30' : 'hover:bg-[hsl(35,20%,92%)]'}`}
              >
                <ShoppingBag className={`w-5 h-5 ${itemCount > 0 ? 'text-[hsl(16,55%,36%)]' : 'text-[hsl(20,17%,12%)]'}`} />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-[10px] font-bold bg-[hsl(16,55%,36%)] text-white rounded-full animate-pulse">
                    {itemCount}
                  </span>
                )}
              </button>

              {/* Mobile toggle */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-[hsl(35,20%,92%)] transition-colors duration-150"
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden overflow-hidden bg-[hsl(38,44%,96%)] border-t border-[hsl(32,17%,84%)]"
            >
              <div className="px-4 py-3 space-y-1">
                {navLinks.map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`block px-3 py-2.5 rounded-lg text-sm font-medium ${
                      location.pathname === link.to
                        ? 'bg-[hsl(35,20%,92%)] text-[hsl(20,17%,12%)]'
                        : 'text-[hsl(22,10%,38%)]'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <a
                  href="https://www.lieferando.de/speisekarte/little-italy-eberbach"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-3 py-2.5 rounded-lg text-sm font-medium text-[hsl(16,55%,36%)]"
                >
                  {t('nav_lieferando')} →
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
      {/* Spacer */}
      <div className={`${isAdmin ? '' : 'h-16 lg:h-[calc(4rem+33px)]'}`} />
    </>
  );
}
